// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { mdiUndo } from "@mdi/js";
import { Button, Form, Input, Popover, Typography } from "antd";
import { RootState } from "store/rootReducer";
import styled from "styled-components";
import { UnitSystem } from "types/units";

import {
  addOrganizationScreenshot,
  deleteOrganizationScreenshot,
  deleteOrganizationUnitDefault,
  editOrganizationScreenshot,
  getOrganizationScreenshots,
  getOrganizationUnitDefault,
  setOrganizationUnitDefault
} from "api/users";

import { ScreenshotPresetEditModel, ScreenshotPresetModel } from "models/model";

import { RadioTab } from "components/base/RadioTabs";
import {
  ErrorContainer,
  SectionActionsContainer,
  SectionContainer,
  SectionHeading,
  SectionHeadingTitleContainer
} from "components/user-settings/components/defaults/shared";
import ScreenshotPresetTable from "components/user-settings/components/screenshot/ScreenshotPresetTable";
import {
  Info,
  InfoIconWrapper,
  InfoTitle,
  StyledRadioTabs
} from "components/user-settings/shared/SharedStyle";
import { useUserSettingsQueryKeys } from "components/user/queries/useUserSettingsDefaults/useUserSettingsDefaultsQuery";

import "./OrganizationDisplaySettings.scss";

const { Title } = Typography;

const ORG_DEFAULT_UNIT_QUERY_KEY = "OrgDefaultUnit";

export const getTableTitleMarkup = (title: string) => {
  return (
    <Title level={5} type="secondary">
      {title}
    </Title>
  );
};

const unitTooltipText =
  "This is the default unit setting for new users. It will not change existing user preferences";

const OrganizationDisplaySettings = () => {
  // state from store
  const user = useSelector((state: RootState) => state.auth.user);
  const organizationId = user?.organization?.id;
  const queryClient = useQueryClient();

  // state
  const [currentPreset, setCurrentPreset] = useState<ScreenshotPresetEditModel>({
    title: "",
    width: "",
    height: "",
    titleFontSize: "",
    axisFontSize: "",
    axisLabelFontSize: "",
    topsLabelsFontSize: "",
    dataLabelsFontSize: "",
    gridLabelsFontSize: "",
    legendTitleSize: "",
    legendFontSize: ""
  });

  // query and mutations
  const {
    data: orgScreenshotPresets,
    isLoading,
    isError,
    refetch
  } = useQuery("OrgScreenshotPreset", () => {
    return getOrganizationScreenshots(user?.organization?.id);
  });
  const addMutation = useMutation(
    async (preset: ScreenshotPresetModel) => {
      await addOrganizationScreenshot(user.organization.id, preset);
    },
    {
      onSuccess: async () => {
        setCurrentPreset({
          title: "",
          width: "",
          height: "",
          axisFontSize: "",
          titleFontSize: "",
          axisLabelFontSize: "",
          topsLabelsFontSize: "",
          dataLabelsFontSize: "",
          gridLabelsFontSize: "",
          legendTitleSize: "",
          legendFontSize: ""
        });
        refetch();
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        if (error.response?.data) {
          toast.error(`An error occurred. ${error.response.data}`);
        } else {
          toast.error("Unable to connect to network.");
        }
      }
    }
  );
  const deleteMutation = useMutation(
    async (preset: ScreenshotPresetModel) => {
      await deleteOrganizationScreenshot(user.organization?.id, preset.id);
    },
    {
      onSuccess: () => {
        toast.success("Successfully deleted preset.");
        refetch();
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        if (error.response?.data) {
          toast.error(`An error occurred. ${error.response.data}`);
        } else {
          toast.error("Unable to connect to network.");
        }
      }
    }
  );
  const editMutation = useMutation(
    async (preset: ScreenshotPresetModel) => {
      return await editOrganizationScreenshot(user.organization?.id, preset.id, preset);
    },
    {
      onSuccess: () => {
        toast.success("Successfully updated screenshot preset.");
        refetch();
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        if (error.response?.data) {
          toast.error(`An error occurred. ${error.response.data}`);
        } else if (error instanceof String) {
          toast.error(`An error occurred: ${error}`);
        } else {
          toast.error("Unable to connect to network.");
        }
        refetch();
      }
    }
  );

  // units-of-measurement query and mutations — section-local state (FR-052); a failure
  // here must not affect the screenshot presets above, and vice versa.
  const {
    data: orgUnitDefault,
    isLoading: isUnitLoading,
    isError: isUnitError
  } = useQuery(
    [ORG_DEFAULT_UNIT_QUERY_KEY, organizationId],
    () => getOrganizationUnitDefault(organizationId),
    { enabled: !!organizationId }
  );

  // With `enabled: false` (no organizationId yet), react-query reports the query as
  // "idle", not "loading" — isUnitLoading is false. Without this, the panel would render
  // the confident "no organization default set" claim before ever asking, for a user whose
  // org id just hasn't loaded yet. Treat "no org id" the same as "still loading".
  const isUnitPending = isUnitLoading || !organizationId;

  // "no organization default set" is the object being null/undefined — never unit 0
  // (Hybrid) being falsy. Hybrid is a real, explicitly-stored value (truth-table row 2).
  const hasSavedUnitDefault = orgUnitDefault != null;

  // The value currently displayed, including an optimistic in-flight save — mirrors
  // DisplaySettings.tsx's `currentUnitValue`. The redundant-click guard and the render must
  // read the same value or they diverge mid-flight (BLOCKING-1, EVA-258 round 2 review): a
  // guard compared against the server value let a second, different click get silently
  // dropped while the UI had already moved on to showing it as selected.
  const [pendingUnit, setPendingUnit] = useState<Maybe<UnitSystem>>(undefined);
  const displayedUnit = pendingUnit ?? orgUnitDefault?.unit;

  const saveUnitMutation = useMutation(
    (unit: UnitSystem) => setOrganizationUnitDefault(organizationId, { unit }),
    {
      onSuccess: () => {
        toast.success("Successfully saved organization default unit of measurement.");
        queryClient.invalidateQueries([ORG_DEFAULT_UNIT_QUERY_KEY, organizationId]);
        queryClient.invalidateQueries(useUserSettingsQueryKeys.userSettings);
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        Sentry.captureException(error, {
          tags: { feature: "org-settings.save-unit-default" },
          extra: { organizationId }
        });
        toast.error(
          typeof error?.response?.data === "string"
            ? error.response.data
            : "Failed to save organization default unit of measurement."
        );
        // The query is deliberately not invalidated on error, so clearing the optimistic
        // value here is what makes `displayedUnit` fall back to the real server value —
        // and because that's a genuine prop change, StyledRadioTabs' own value-sync effect
        // resets it without needing a remount (FR-035).
        setPendingUnit(undefined);
      }
    }
  );

  const clearUnitMutation = useMutation(
    () => deleteOrganizationUnitDefault(organizationId),
    {
      onSuccess: () => {
        toast.success(
          "Reset the organization default unit of measurement — users now fall back to the system default."
        );
        // A successful Reset always means "no default" — clear any leftover optimistic
        // value so a save that resolved moments earlier can't leave `displayedUnit` stuck
        // pointing at a value that's no longer true.
        setPendingUnit(undefined);
        queryClient.invalidateQueries([ORG_DEFAULT_UNIT_QUERY_KEY, organizationId]);
        queryClient.invalidateQueries(useUserSettingsQueryKeys.userSettings);
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        Sentry.captureException(error, {
          tags: { feature: "org-settings.clear-unit-default" },
          extra: { organizationId }
        });
        toast.error(
          typeof error?.response?.data === "string"
            ? error.response.data
            : "Failed to reset organization default unit of measurement."
        );
      }
    }
  );

  // Once the server value catches up to what we optimistically showed, drop the override so
  // a later out-of-band refetch (e.g. window focus) can't be masked by a stale pending value.
  useEffect(() => {
    if (pendingUnit !== undefined && orgUnitDefault?.unit === pendingUnit) {
      setPendingUnit(undefined);
    }
  }, [orgUnitDefault?.unit, pendingUnit]);

  // Disables the radio group for the duration of either mutation — closes the same-click
  // race at the source (no two saves can ever be in flight at once) and gives the control
  // an in-flight affordance it otherwise has none of.
  const unitControlsDisabled = saveUnitMutation.isLoading || clearUnitMutation.isLoading;

  function handleUnitChange(unit: UnitSystem) {
    if (!organizationId || unitControlsDisabled) return;
    // Guard against the value currently displayed (including an in-flight optimistic pick),
    // not the last server-confirmed value — skip it so the audit trail never records a
    // change with an identical before/after (R10).
    if (unit === displayedUnit) return;
    setPendingUnit(unit);
    saveUnitMutation.mutate(unit);
  }

  function handleUnitClear() {
    // Mirrors handleUnitChange: without the in-flight guard a DELETE can fire while a PUT
    // is still outstanding, and the stored value becomes whichever the server finishes
    // last rather than what either click meant.
    if (!organizationId || unitControlsDisabled) return;
    clearUnitMutation.mutate();
  }

  // handlers
  function validPreset(preset: ScreenshotPresetEditModel) {
    return Object.keys(preset).every((k) => preset[k].length || preset[k]);
  }

  function handleFormChange(changedValue) {
    const key = Object.keys(changedValue)[0];
    const value = changedValue[key];
    const nextValue = key !== "title" ? value.replace(/[^\d]+/g, "") : value;
    setCurrentPreset({ ...currentPreset, [key]: nextValue });
  }

  async function handleAddPreset() {
    if (!validPreset(currentPreset)) return;

    await addMutation.mutate({
      title: currentPreset.title,
      width: Number(currentPreset.width),
      height: Number(currentPreset.height),
      axisFontSize: Number(currentPreset.axisFontSize),
      titleFontSize: Number(currentPreset.titleFontSize),
      axisLabelFontSize: Number(currentPreset.axisLabelFontSize),
      topsLabelsFontSize: Number(currentPreset.topsLabelsFontSize),
      dataLabelsFontSize: Number(currentPreset.dataLabelsFontSize),
      gridLabelsFontSize: Number(currentPreset.gridLabelsFontSize),
      legendTitleSize: Number(currentPreset.legendTitleSize),
      legendFontSize: Number(currentPreset.legendFontSize)
    });
  }

  async function handleEditPreset(preset: ScreenshotPresetModel) {
    editMutation.mutate(preset);
  }

  async function handleDeletePreset(preset: ScreenshotPresetModel) {
    deleteMutation.mutate(preset);
  }

  return (
    <RootContainer>
      <SectionContainer>
        <SectionHeading>
          <SectionHeadingTitleContainer>
            <InfoTitle level={3}>
              Unit of Measurement
              <Popover content={<Info>{unitTooltipText}</Info>} placement="right">
                <InfoIconWrapper />
              </Popover>
            </InfoTitle>
          </SectionHeadingTitleContainer>
          {hasSavedUnitDefault && !isUnitError && (
            <SectionActionsContainer>
              <Button
                type="text"
                data-testid="reset-org-unit-default-button"
                loading={clearUnitMutation.isLoading}
                disabled={unitControlsDisabled}
                onClick={handleUnitClear}
                icon={<Icon path={mdiUndo} size={1} />}>
                Reset
              </Button>
            </SectionActionsContainer>
          )}
        </SectionHeading>

        {isUnitPending && <div data-testid="org-unit-default-loading">Loading...</div>}

        {!isUnitPending && isUnitError && (
          <ErrorContainer data-testid="org-unit-default-error">
            Unable to connect to backend services.
          </ErrorContainer>
        )}

        {!isUnitPending && !isUnitError && (
          <StyledRadioTabs
            name="orgUnitsMeasures"
            value={displayedUnit}
            disabled={unitControlsDisabled}
            onChange={handleUnitChange}>
            <RadioTab
              value={UnitSystem.Hybrid}
              label="Hybrid"
              isDisabled={unitControlsDisabled}
            />
            <RadioTab
              value={UnitSystem.Imperial}
              label="US Imperial"
              isDisabled={unitControlsDisabled}
            />
          </StyledRadioTabs>
        )}
      </SectionContainer>

      <div className="organizationAccountHeader">
        <Title level={3}>Screenshot Presets</Title>
      </div>
      <ContentContainer>
        {isError ? (
          <div data-testid="org-screenshot-presets-error">
            Unable to connect to backend services.
          </div>
        ) : (
          <>
            <div className="section-heading">Organization Screenshot Presets</div>
            <ScreenshotPresetTable
              dataSource={orgScreenshotPresets ?? []}
              editable
              loading={isLoading}
              title={getTableTitleMarkup("Organization Screenshot Presets")}
              onChange={handleEditPreset}
              onDelete={handleDeletePreset}
            />

            <StyledForm layout="vertical" size="large" onValuesChange={handleFormChange}>
              <Form.Item name="title" label="Title">
                <Input placeholder="Screenshot title" />
              </Form.Item>

              <Form.Item name="width" label="Width">
                <Input placeholder="px" />
              </Form.Item>

              <Form.Item name="height" label="Height">
                <Input placeholder="px" />
              </Form.Item>

              <Form.Item name="titleFontSize" label="Title Size">
                <Input placeholder="pt" />
              </Form.Item>

              <Form.Item name="axisFontSize" label="Axis Title">
                <Input placeholder="pt" />
              </Form.Item>

              <Form.Item name="axisLabelFontSize" label="Axis Labels">
                <Input placeholder="pt" />
              </Form.Item>

              <Form.Item name="topsLabelsFontSize" label="Tops Labels">
                <Input placeholder="pt" />
              </Form.Item>

              <Form.Item name="dataLabelsFontSize" label="Data Labels">
                <Input placeholder="pt" />
              </Form.Item>

              <Form.Item name="gridLabelsFontSize" label="Grid Labels">
                <Input placeholder="pt" />
              </Form.Item>

              <Form.Item name="legendTitleSize" label="Legend Title Size">
                <Input placeholder="pt" />
              </Form.Item>

              <Form.Item name="legendFontSize" label="Legend Label Size">
                <Input placeholder="pt" />
              </Form.Item>

              <Button
                type="text"
                loading={addMutation.isLoading}
                onClick={handleAddPreset}>
                + Add
              </Button>
            </StyledForm>
          </>
        )}
      </ContentContainer>
    </RootContainer>
  );
};

export default OrganizationDisplaySettings;

const RootContainer = styled.div`
  gap: 10px;
  width: 100%;
  display: grid;
  grid-template-rows: auto 1fr;
  text-align: left;
  overflow: auto;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto minmax(0, 1fr);
  .ant-divider-horizontal {
    margin: 5px 0;
  }
  .section {
    padding: 33px 0;
  }
  .section-heading {
    font-size: 1.6rem;
    padding-bottom: 22px;
    font-weight: var(--fontWeightMedium);
    padding: 0;
  }
  .units-measurements {
    .section-heading {
      padding-bottom: 15px;
    }
    .RadioTab.isChecked {
      background: var(--color-text);
      border-color: var(--color-text);
    }
  }
  .preset-category {
    padding-left: 10px;
    margin-top: 5px;
    .category-heading {
      font-size: 1.4rem;
      font-weight: var(--fontWeightMedium);
    }
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    /* display: none; <- Crashes Chrome on hover */
    -webkit-appearance: none;
    margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
  }

  input[type="number"] {
    -moz-appearance: textfield; /* Firefox */
  }
`;
const ContentContainer = styled.div`
  min-width: 768px;
  overflow: auto;
`;

const StyledForm = styled(Form)`
  display: grid;
  align-items: flex-end;
  grid-template-columns: 200px repeat(9, minmax(0, 1fr)) max-content;
  grid-column-gap: 12px;
  margin-top: 36px;

  .ant-form-item {
    margin-bottom: 0; // to adjust +Add button alignment with input fields
  }
  .ant-form-item-label {
    font-weight: 500;
  }
  .ant-form-item-label > label {
    height: unset; // removes default ant label height of 40px
  }
  .ant-form-item:not(:first-child) .ant-input {
    text-align: right; // align text to the right for all input fields except the first one
  }
`;
